import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeftRight, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ActionCell } from "@/components/shared/action-cell";
import { ConfigFormDialog } from "@/components/shared/config-form-dialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { DataToolbar } from "@/components/shared/data-toolbar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { ReorderButtons } from "@/components/shared/reorder-buttons";
import { Button } from "@/components/ui/button";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  type ChangeReason,
  useChangeReasons,
  useCreateChangeReason,
  useDeleteChangeReason,
  useReorderChangeReason,
  useUpdateChangeReason,
} from "@/hooks/use-change-reasons";
import { type ChangeReasonCreateInput, changeReasonCreateSchema } from "@/schemas/change-reasons";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; reason: ChangeReason }
  | { type: "delete"; reason: ChangeReason };

export function SettingsChangeReasonsPage() {
  const { data: reasons = [], isLoading, isError, error, refetch } = useChangeReasons();
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });
  const deleteReason = useDeleteChangeReason();
  const reorder = useReorderChangeReason();

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load change reasons"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        The reasons available when recording why an organizational change happened — a promotion, a
        restructure, a role change.
      </p>

      <DataToolbar
        actions={
          <Button onClick={() => setDialog({ type: "create" })}>
            <Plus className="mr-2 h-4 w-4" />
            Add reason
          </Button>
        }
      />

      <DataTable
        headers={[
          { label: "Order", className: "w-24" },
          { label: "Name" },
          { label: "Description" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={ArrowLeftRight}
            title="No change reasons"
            description="Create the first reason to describe why changes happen."
          />
        }
      >
        {reasons.map((reason, index) => (
          <tr
            key={reason.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3">
              <ReorderButtons
                label={reason.name}
                disableUp={index === 0 || reorder.isPending}
                disableDown={index === reasons.length - 1 || reorder.isPending}
                onMove={(direction) => reorder.mutate({ id: reason.id, direction })}
              />
            </td>
            <td className="px-4 py-3 font-medium">{reason.name}</td>
            <td className="px-4 py-3 text-muted-foreground">{reason.description || "—"}</td>
            <td className="px-4 py-3 text-right">
              <ActionCell
                items={[
                  {
                    icon: Pencil,
                    label: "Edit",
                    onClick: () => setDialog({ type: "edit", reason }),
                  },
                  {
                    icon: Trash2,
                    label: "Delete",
                    onClick: () => setDialog({ type: "delete", reason }),
                    variant: "destructive",
                    separatorBefore: true,
                  },
                ]}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      {(dialog.type === "create" || dialog.type === "edit") && (
        <ChangeReasonDialog
          open
          onOpenChange={() => setDialog({ type: "none" })}
          reason={dialog.type === "edit" ? dialog.reason : undefined}
        />
      )}

      {dialog.type === "delete" && (
        <ConfirmDialog
          open
          onOpenChange={() => setDialog({ type: "none" })}
          title={`Delete "${dialog.reason.name}"?`}
          description="This removes the reason from the list. Past records that referenced it keep their history."
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            deleteReason.mutate(dialog.reason.id, {
              onSuccess: () => setDialog({ type: "none" }),
            });
          }}
        />
      )}
    </div>
  );
}

function ChangeReasonDialog({
  open,
  onOpenChange,
  reason,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason?: ChangeReason;
}) {
  const isEdit = reason != null;
  const create = useCreateChangeReason();
  const update = useUpdateChangeReason(reason?.id ?? "");
  const form = useForm<ChangeReasonCreateInput>({
    resolver: zodResolver(changeReasonCreateSchema),
    defaultValues: {
      name: reason?.name ?? "",
      description: reason?.description ?? "",
    },
  });

  async function onSubmit(values: ChangeReasonCreateInput) {
    const payload = {
      ...values,
      description: values.description?.trim() || null,
    };
    if (isEdit) await update.mutateAsync(payload);
    else await create.mutateAsync(payload);
    form.reset();
    onOpenChange(false);
  }

  const pending = create.isPending || update.isPending;

  return (
    <ConfigFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? "Edit reason" : "Add reason"}
      description={
        isEdit ? "Update this change reason." : "Add a new reason for organizational changes."
      }
      form={form}
      onSubmit={onSubmit}
      submitLabel={isEdit ? "Save" : "Create"}
      pending={pending}
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Promotion" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                value={field.value ?? ""}
                rows={3}
                placeholder="Optional short note"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </ConfigFormDialog>
  );
}
